import React, { useEffect, useState } from 'react';
import { Select } from '@monavate/ui-core';
import { Input } from '@monavate/ui-core';
import { MessageBlock } from '@monavate/ui-core';
import styled from 'styled-components';
import { getCurrencySign } from '@/helpers/currency';
import { RootState, useAppSelector } from '@/reduxStore/rootReducer';
import { WarningText } from '@monavate/ui-core';
import { useSelector } from 'react-redux';
import { GroupedSwitcherPills } from '@monavate/ui-core';
import { Col, Row } from 'antd';

const TypeContainer = styled.div`
    display: flex;
    flex-direction: column;
`;

const InputsContainer = styled.div`
    width: 100%;
    margin-top: 20px;
`;

interface SimulateTransactionFormProps {
    register: any;
    setValue: any;
    watch: any;
    errors: any;
    enabledCurrencies: string[];
    selectedType: string;
    setSelectedType: (id: string) => void;
    currentCard: any;
    step: number;
}

const transactionTypes = [
    { value: 'authorisation', label: 'Authorisation' },
    { value: 'purchase', label: 'Purchase' },
];

const SimulateTransactionForm: React.FC<SimulateTransactionFormProps> = ({
    register,
    setValue,
    errors,
    watch,
    enabledCurrencies,
    selectedType,
    setSelectedType,
    currentCard,
    step,
}) => {
    const paymentInstructionToDisplay = useSelector(
        (state: RootState) => state.orders.paymentInstructionToDisplay,
    );
    const [showBillingWarning, setShowBillingWarning] = useState(false);
    const [showCountryWarning, setShowCountryWarning] = useState(false);
    const [showMccWarning, setShowMccWarning] = useState(false);
    const [amountBlur, setAmountBlur] = useState(false);
    const countriesList = useAppSelector((state: RootState) => state.commonTerms.countries);
    const mccList = useAppSelector((state: RootState) => state.commonTerms.mccList);
    const allFieldsWatched = watch();

    const onChaneType = (id: string) => {
        setSelectedType(id);
        setValue('type', id);
    };

    useEffect(() => {
        if (
            allFieldsWatched.type === 'authorisation' &&
            allFieldsWatched.billingAmount >
                paymentInstructionToDisplay.amount +
                    (paymentInstructionToDisplay.tolerance * paymentInstructionToDisplay.amount) /
                        100
        ) {
            setShowBillingWarning(true);
        } else {
            setShowBillingWarning(false);
        }
    }, [allFieldsWatched.billingAmount, allFieldsWatched.type]);

    useEffect(() => {
        if (
            (currentCard?.countryDenyList &&
                currentCard?.countryDenyList?.includes(allFieldsWatched.countryCode)) ||
            (currentCard?.countryAllowList &&
                !currentCard?.countryAllowList?.includes(allFieldsWatched.countryCode))
        ) {
            setShowCountryWarning(true);
        } else {
            setShowCountryWarning(false);
        }
    }, [allFieldsWatched.countryCode]);

    useEffect(() => {
        if (
            (currentCard?.mccDenyList &&
                currentCard?.mccDenyList?.includes(allFieldsWatched.mcc)) ||
            (currentCard?.mccAllowList &&
                !currentCard?.mccAllowList?.includes(allFieldsWatched.mcc))
        ) {
            setShowMccWarning(true);
        } else {
            setShowMccWarning(false);
        }
    }, [allFieldsWatched.mcc]);

    useEffect(() => {
        setShowBillingWarning(false);
        setShowCountryWarning(false);
        setShowMccWarning(false);
    }, [step]);

    return (
        <>
            <TypeContainer>
                <GroupedSwitcherPills
                    items={transactionTypes}
                    title={'Transaction type'}
                    selectedItemId={selectedType}
                    onChange={onChaneType}
                />
            </TypeContainer>

            <InputsContainer>
                <Row gutter={[24, 24]}>
                    <Col span={12}>
                        <Select
                            value={allFieldsWatched.acquirerCurrency || null}
                            label="Acquirer currency"
                            placeholder="Select Currency"
                            options={enabledCurrencies.map((name: string) => ({
                                label: name,
                                value: name,
                            }))}
                            showSearch={false}
                            onChange={(currency: string) => {
                                setValue('acquirerCurrency', currency, { shouldValidate: true });
                            }}
                            required
                            error={errors?.acquirerCurrency?.message?.toString()}
                        />
                    </Col>
                    <Col span={12}>
                        <Input
                            label="Acquirer amount"
                            required
                            handleBlur={() => {
                                if (watch('acquirerAmount')) setAmountBlur(true);
                            }}
                            addonBefore={getCurrencySign(allFieldsWatched.acquirerCurrency || '')}
                            register={() => register('acquirerAmount')}
                            onKeyUp={(e: any) => {
                                if (!amountBlur) {
                                    setValue('billingAmount', e.target.value, {
                                        shouldValidate: true,
                                    });
                                }
                            }}
                            error={errors.acquirerAmount?.message}
                        />
                    </Col>
                    <Col style={{ marginTop: -24 }}>
                        <MessageBlock styles={{ padding: '0', marginTop: '10px' }} transparent>
                            Amount of transaction in the currency of acquirer.
                        </MessageBlock>
                    </Col>
                    <Col span={12}>
                        <Select
                            disabled
                            value={allFieldsWatched.billingCurrency || null}
                            label="Billing currency"
                            options={enabledCurrencies.map((name: string) => ({
                                label: name,
                                value: name,
                            }))}
                            showSearch={false}
                            warning={showBillingWarning}
                            required
                            error={errors?.billingCurrency?.message?.toString()}
                        />
                    </Col>
                    <Col span={12}>
                        <Input
                            label="Billing amount"
                            required
                            addonBefore={getCurrencySign(allFieldsWatched.billingCurrency || '')}
                            register={() => register('billingAmount')}
                            warning={showBillingWarning}
                            error={errors.billingAmount?.message}
                        />
                    </Col>
                    <Col style={{ marginTop: -24 }}>
                        {showBillingWarning ? (
                            <WarningText styles={{ padding: '0', marginTop: '10px' }} transparent>
                                Amount exceeds payment authorisation tolerance - authorisation will
                                be declined.
                            </WarningText>
                        ) : (
                            <MessageBlock styles={{ padding: '0', marginTop: '10px' }} transparent>
                                Amount of transaction in the card’s billing currency.
                            </MessageBlock>
                        )}
                    </Col>
                    <Col span={12}>
                        <Select
                            label="Card acceptor country"
                            required
                            value={allFieldsWatched.countryCode || null}
                            options={countriesList.map(
                                ({ alpha3Code, name }: { alpha3Code: string; name: string }) => ({
                                    value: alpha3Code,
                                    label: name,
                                }),
                            )}
                            placeholder="Select country of recipient"
                            showSearch
                            warning={showCountryWarning}
                            onChange={(country: string) => {
                                setValue('countryCode', country, { shouldValidate: true });
                            }}
                            error={errors.countryCode?.message}
                        />
                    </Col>
                    <Col span={12}>
                        <Select
                            label="Card acceptor MCC"
                            required
                            value={allFieldsWatched.mcc || null}
                            options={mccList.map(
                                ({ id, title }: { id: string; title: string }) => ({
                                    value: id,
                                    label: title,
                                }),
                            )}
                            warning={showMccWarning}
                            placeholder="MCC"
                            showSearch
                            onChange={(mcc: string) => {
                                setValue('mcc', mcc, { shouldValidate: true });
                            }}
                            error={errors.mcc?.message}
                        />
                    </Col>
                    {(showCountryWarning || showMccWarning) &&
                        allFieldsWatched.type === 'authorisation' && (
                            <Col style={{ marginTop: -24 }}>
                                {showCountryWarning && (
                                    <WarningText
                                        styles={{ padding: '0', marginTop: '10px' }}
                                        transparent
                                    >
                                        Country not allowed for this payment, authorisation will be
                                        rejected.
                                    </WarningText>
                                )}
                                {showMccWarning && (
                                    <WarningText
                                        styles={{ padding: '0', marginTop: '10px' }}
                                        transparent
                                    >
                                        MCC not allowed for this payment, authorisation will be
                                        rejected.
                                    </WarningText>
                                )}
                            </Col>
                        )}

                    <Col span={24}>
                        <Input
                            label="Authorisation transaction ID"
                            tooltip="The ID of preceding authorisation on the transaction. Not required for offline transactions."
                            register={() => register('transactionId')}
                            error={errors.transactionId?.message}
                        />
                    </Col>
                </Row>
            </InputsContainer>
        </>
    );
};

export default SimulateTransactionForm;
